/**
 * Warranty Comparison Widget
 * Displays standard vs extended warranty comparison with add-to-cart functionality
 *
 * Features:
 * - Detects warranty eligibility from battery voltage tags
 * - Compares standard (6 months) vs extended (12 months) warranty
 * - Displays comprehensive comparison table in x-modal
 * - Fetches warranty addon products from bundle-config.json
 * - Integrates with Shopify product API for real-time pricing
 * - Calculates discounted prices based on current bundle tier
 * - Add to cart functionality with cart refresh
 * - British English throughout (labour, honour, favour, colour)
 * - All SVG icons inline
 * - Theme token styling
 *
 * @version 1.0.0
 */

(function(window) {
  'use strict';

  class WarrantyComparisonWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      // Widget state
      this.isEligible = false;
      this.extendedWarrantyAddon = null;
      this.batteryWarrantyAddon = null;
      this.bundleConfig = null;
      this.extendedVariantId = null;
      this.batteryVariantId = null;
      this.modalId = `warranty-modal-${config.id}`;

      // Warranty data structure (will be populated dynamically based on product)
      this.warrantyData = {
        standard: null,
        extended: null
      };

      // Comparison table rows (will be populated dynamically based on product)
      this.comparisonRows = [];
    }

    /**
     * Initialize widget - check eligibility and load warranty data
     */
    async init() {
      // Set up product data from config (parent product - the ride-on)
      this.productData = {
        id: this.config.productId || null,
        tags: (this.config.productTags && this.config.productTags.length > 0) ? this.config.productTags : [],
        title: this.config.productTitle || '',
        description: this.config.productDescription || '',
        handle: this.config.productHandle || '',
        vendor: this.config.productVendor || '',
        metafields: this.config.productMetafields || {},
        variants: this.config.productVariants || []
      };

      // Detect product attributes
      this.productAttributes = this.detectProductAttributes();

      // Generate dynamic warranty data based on product attributes
      this.generateWarrantyData();

      // Load bundle config
      this.bundleConfig = this.manager.bundleConfig;

      if (!this.bundleConfig) {
        console.warn('[WarrantyComparisonWidget] Bundle config not available');
        return;
      }

      // Widget is eligible if it's a ride-on product
      this.isEligible = this.productAttributes.isRideOn;

      if (!this.isEligible) {
        return;
      }

      // Find warranty addons from protection category and check eligibility
      await this.loadWarrantyAddons();
    }

    /**
     * Detect product attributes from tags and vendor
     * @returns {Object} Product attributes (vendor, powerType, isRideOn)
     */
    detectProductAttributes() {
      const tags = this.productData.tags || [];
      const vendor = this.productData.vendor || '';
      const handle = this.productData.handle || '';

      // Detect OneMoto products by:
      // 1. Vendor name contains "onemoto"
      // 2. Product handle contains "onemoto", "onemx", "onemonkey"
      // 3. Tags include "OneMoto"
      const isOneMoto = vendor.toLowerCase().includes('onemoto')
        || handle.toLowerCase().includes('onemoto')
        || handle.toLowerCase().includes('onemx')
        || handle.toLowerCase().includes('onemonkey')
        || tags.some(tag => tag.toLowerCase() === 'onemoto');

      // Detect RiiRoo products (only if NOT OneMoto)
      const isRiiRoo = !isOneMoto && vendor.toLowerCase().includes('riiroo');

      // Detect power type from tags
      let powerType = 'unknown';
      let hasBattery = false;

      for (const tag of tags) {
        if (tag.match(/Battery Voltage_/i)) {
          powerType = 'battery';
          hasBattery = true;
          break;
        } else if (tag.match(/Petrol Powered/i) || tag.match(/Fuel Type_Petrol/i) || tag.match(/Power Type_Petrol/i) || tag.match(/Petrol_/i)) {
          powerType = 'petrol';
        } else if (tag.match(/Foot Powered/i) || tag.match(/Type_Pedal/i)) {
          powerType = 'foot';
        }
      }

      // Check if ride-on product
      const isRideOn = tags.some(tag => tag.match(/Product Type_Ride Ons/i));

      return {
        vendor: isRiiRoo ? 'RiiRoo' : isOneMoto ? 'OneMoto' : vendor,
        isRiiRoo,
        isOneMoto,
        powerType,
        hasBattery,
        isRideOn
      };
    }

    /**
     * Generate dynamic warranty data based on product attributes
     */
    generateWarrantyData() {
      const { powerType, vendor, hasBattery, isRiiRoo, isOneMoto } = this.productAttributes;

      // Determine standard warranty duration based on vendor and power type
      let standardDuration = '6 months';
      let standardBadge = 'Included Free';

      if (isOneMoto) {
        // OneMoto: 12 months for motors/controllers/chargers, 30 days for consumables
        // Petrol is 30 days only
        standardDuration = powerType === 'petrol' ? '30 days' : '12 months';
      } else if (isRiiRoo) {
        // RiiRoo: 6 months standard, 30 days for petrol
        standardDuration = powerType === 'petrol' ? '30 days' : '6 months';
      }

      // Build dynamic highlights for standard warranty
      const standardHighlights = [];

      if (isRiiRoo) {
        if (powerType === 'battery') {
          standardHighlights.push('6-month warranty on motors & electrical components');
          standardHighlights.push('Battery not covered (upgrade available)');
          standardHighlights.push('Parts sent out first, then repair if needed');
        } else if (powerType === 'petrol') {
          standardHighlights.push('30-day manufacturer defect warranty only');
          standardHighlights.push('Parts supply or repair at cost after 30 days');
          standardHighlights.push('Higher risk of misuse or incorrect assembly');
        } else {
          standardHighlights.push('Manufacturing defects covered');
          standardHighlights.push('Faulty components replaced');
          standardHighlights.push('Simple claims process');
        }
      } else if (isOneMoto) {
        if (powerType === 'battery') {
          standardHighlights.push('12-month warranty on motors & electrical components');
          standardHighlights.push('Battery not covered under warranty');
          standardHighlights.push('Parts sent out first, then repair if needed');
        } else if (powerType === 'petrol') {
          standardHighlights.push('30-day warranty + 12-month structural coverage');
          standardHighlights.push('Customer responsible for maintenance after 30 days');
          standardHighlights.push('Parts supply available at cost after 30 days');
        } else {
          standardHighlights.push('12-month manufacturer defect warranty');
          standardHighlights.push('Manufacturing defects covered');
          standardHighlights.push('Parts sent out for quick repairs');
        }
      }

      // Build dynamic highlights for extended warranty (RiiRoo only - 12 month comprehensive)
      const extendedHighlights = [];

      if (isRiiRoo) {
        extendedHighlights.push('Full 12-month comprehensive electrical coverage');
        extendedHighlights.push('Battery included (full 12 months)');
        extendedHighlights.push('Total peace of mind protection');
      }

      // Set warranty data
      this.warrantyData = {
        standard: {
          name: 'Standard Warranty',
          duration: standardDuration,
          batteryDuration: '30 days', // Both vendors: batteries are 30 days
          price: 'Included',
          badge: standardBadge,
          badgeColour: '34, 197, 94', // green
          highlights: standardHighlights
        },
        extended: {
          name: '12-Month Comprehensive Warranty',
          duration: '12 months',
          batteryDuration: '12 months', // Extended warranty includes 12-month battery
          price: null, // Fetched from Shopify
          badge: 'Recommended',
          badgeColour: '59, 130, 246', // blue
          productHandle: 'riiroo-extended-warranty',
          highlights: extendedHighlights
        }
      };

      // Update comparison rows based on vendor and power type
      this.comparisonRows = [
        {
          label: 'Coverage Period',
          icon: 'calendar',
          standard: `${standardDuration} from purchase`,
          extended: '12 months from purchase',
          highlight: true
        }
      ];

      // Add battery row only for battery-powered products
      if (powerType === 'battery' && hasBattery) {
        this.comparisonRows.push({
          label: 'Battery Warranty',
          icon: 'battery',
          standard: 'Not covered',
          extended: isRiiRoo ? '12 months included' : 'Not available',
          highlight: true
        });
      }

      // Add component-specific rows based on power type and vendor
      if (powerType === 'battery') {
        if (isRiiRoo) {
          this.comparisonRows.push(
            {
              label: 'Motors & Controllers',
              icon: 'check',
              standard: '6 months',
              extended: '12 months',
              highlight: false
            },
            {
              label: 'Chargers & Displays',
              icon: 'check',
              standard: '6 months',
              extended: '12 months',
              highlight: false
            },
            {
              label: 'Parental Remotes',
              icon: 'check',
              standard: '12 months',
              extended: '12 months',
              highlight: false
            },
            {
              label: 'Wheels & Media Display',
              icon: 'check',
              standard: '12 months',
              extended: '12 months',
              highlight: false
            }
          );
        } else if (isOneMoto) {
          this.comparisonRows.push(
            {
              label: 'Motors & Controllers',
              icon: 'check',
              standard: '12 months',
              extended: 'Not available',
              highlight: false
            },
            {
              label: 'Chargers',
              icon: 'check',
              standard: '12 months',
              extended: 'Not available',
              highlight: false
            }
          );
        }
      } else if (powerType === 'petrol') {
        this.comparisonRows.push(
          {
            label: 'Engine & Parts',
            icon: 'check',
            standard: '30 days only',
            extended: isRiiRoo ? 'Not available for petrol' : 'Not available',
            highlight: false
          },
          {
            label: 'Parts Supply',
            icon: 'check',
            standard: 'At cost after 30 days',
            extended: isRiiRoo ? 'Not available for petrol' : 'Not available',
            highlight: false
          }
        );
      } else {
        this.comparisonRows.push(
          {
            label: 'Parts Coverage',
            icon: 'check',
            standard: 'Included',
            extended: isRiiRoo ? '12 months' : 'Not available',
            highlight: false
          }
        );
      }

        // Add consumables row - covered under extended warranty
        this.comparisonRows.push({
          label: 'Consumables',
          icon: 'check',
          standard: 'Not covered',
          extended: isRiiRoo ? '12 months' : 'Not available',
          highlight: false
        });

      // Add common exclusions row
      this.comparisonRows.push({
        label: 'What\'s NOT Covered',
        icon: 'x',
        standard: 'Misuse, water damage, wear & tear, etc.',
        extended: isRiiRoo ? 'Misuse, water damage, wear & tear, etc.' : 'Not available',
        highlight: false
      });
    }

    /**
     * Get "What's Covered" text based on power type
     */
    getWhatsCoveredText(powerType) {
      if (powerType === 'battery') {
        return 'Manufacturing defects, faulty components, motor/electronics, battery issues';
      } else if (powerType === 'petrol') {
        return 'Manufacturing defects, faulty components, engine issues, workmanship';
      } else {
        return 'Manufacturing defects, faulty components, workmanship issues';
      }
    }

    /**
     * Check if addon is eligible for current product (using bundle adapter logic)
     * @param {Object} addonConfig - Addon configuration from bundle-config.json
     * @returns {boolean} True if addon is eligible
     */
    isAddonEligible(addonConfig) {
      if (!this.productData || !this.productData.tags) return false;

      const productTags = this.productData.tags;

      // Check specific products
      if (addonConfig.specificProducts && addonConfig.specificProducts.length > 0) {
        return addonConfig.specificProducts.includes(this.productData.handle);
      }

      // Check exclude tags
      if (addonConfig.excludeTags && addonConfig.excludeTags.length > 0) {
        const hasExcludedTag = addonConfig.excludeTags.some(tag => productTags.includes(tag));
        if (hasExcludedTag) return false;
      }

      // Check eligibility tags
      if (addonConfig.eligibilityTags && addonConfig.eligibilityTags.length > 0) {
        return addonConfig.eligibilityTags.some(tag => productTags.includes(tag));
      }

      return true;
    }

    /**
     * Load warranty addons from bundle config protection category
     */
    async loadWarrantyAddons() {
      const protectionAddons = this.bundleConfig?.addons?.protection || [];

      // Find extended warranty addon
      const extendedAddonConfig = protectionAddons.find(
        addon => addon.productHandle === 'riiroo-extended-warranty'
      );

      // Check if extended warranty is eligible for this product
      if (extendedAddonConfig && this.isAddonEligible(extendedAddonConfig)) {
        this.extendedWarrantyAddon = extendedAddonConfig;
        await this.fetchExtendedWarrantyData();
      } else {
        this.extendedWarrantyAddon = null;
      }

      // Find battery warranty addon
      const batteryAddonConfig = protectionAddons.find(
        addon => addon.productHandle === 'riiroo-12-month-ride-on-battery-warranty'
      );

      // Check if battery warranty is eligible for this product
      if (batteryAddonConfig && this.isAddonEligible(batteryAddonConfig)) {
        this.batteryWarrantyAddon = batteryAddonConfig;
        await this.fetchBatteryWarrantyData();
      } else {
        this.batteryWarrantyAddon = null;
      }
    }

    /**
     * Fetch extended warranty product data from Shopify API
     */
    async fetchExtendedWarrantyData() {
      if (!this.extendedWarrantyAddon || !this.extendedWarrantyAddon.productHandle) {
        return;
      }

      try {
        const response = await fetch(`/products/${this.extendedWarrantyAddon.productHandle}.js`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const product = await response.json();

        // Store product data
        this.extendedWarrantyAddon.productData = product;

        // Get first available variant
        if (product.variants && product.variants.length > 0) {
          this.extendedVariantId = product.variants[0].id;
          this.extendedWarrantyAddon.actualPrice = product.variants[0].price;
          this.extendedWarrantyAddon.compareAtPrice = product.variants[0].compare_at_price || null;
        }

      } catch (error) {
        console.warn('[WarrantyComparisonWidget] Could not fetch extended warranty data:', error);
        // Fallback to tierValue from bundle config
        this.extendedWarrantyAddon.actualPrice = this.extendedWarrantyAddon.tierValue * 100; // Convert to cents
      }
    }

    /**
     * Fetch battery warranty product data from Shopify API
     */
    async fetchBatteryWarrantyData() {
      if (!this.batteryWarrantyAddon || !this.batteryWarrantyAddon.productHandle) {
        return;
      }

      try {
        const response = await fetch(`/products/${this.batteryWarrantyAddon.productHandle}.js`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const product = await response.json();

        // Store product data
        this.batteryWarrantyAddon.productData = product;

        // Get first available variant
        if (product.variants && product.variants.length > 0) {
          this.batteryVariantId = product.variants[0].id;
          this.batteryWarrantyAddon.actualPrice = product.variants[0].price;
          this.batteryWarrantyAddon.compareAtPrice = product.variants[0].compare_at_price || null;
        }

      } catch (error) {
        console.warn('[WarrantyComparisonWidget] Could not fetch battery warranty data:', error);
        // Fallback to tierValue from bundle config
        this.batteryWarrantyAddon.actualPrice = this.batteryWarrantyAddon.tierValue * 100; // Convert to cents
      }
    }

    /**
     * Validate parent stock on initial render and disable buttons if unavailable
     */
    async validateParentStockOnRender() {
      const stockCheck = this.checkParentStock();

      if (!stockCheck.available) {
        console.warn('[WarrantyComparisonWidget] Parent product out of stock on render:', stockCheck.message);

        // Disable extended warranty button
        const extendedButton = document.getElementById(`add-warranty-btn-${this.modalId}`);
        if (extendedButton) {
          extendedButton.disabled = true;
          extendedButton.style.opacity = '0.5';
          extendedButton.style.cursor = 'not-allowed';
          extendedButton.style.pointerEvents = 'none';
          extendedButton.style.backgroundColor = '#9ca3af';
          extendedButton.textContent = 'Parent Out of Stock';
        }

        // Disable battery warranty button
        const batteryButton = document.getElementById(`add-battery-warranty-btn-${this.modalId}`);
        if (batteryButton) {
          batteryButton.disabled = true;
          batteryButton.style.opacity = '0.5';
          batteryButton.style.cursor = 'not-allowed';
          batteryButton.style.pointerEvents = 'none';
          batteryButton.style.backgroundColor = '#9ca3af';
          batteryButton.textContent = 'Parent Out of Stock';
        }

        // Add notice in extended warranty modal
        const extendedModal = document.querySelector(`#extended-warranty-modal-${this.modalId}`);
        if (extendedModal) {
          const notice = document.createElement('div');
          notice.style.cssText = 'padding: 12px; margin: 16px; background: rgb(239, 68, 68, 0.1); border-radius: 8px; color: rgb(239, 68, 68); text-align: center; font-size: 14px; font-weight: 500;';
          notice.textContent = 'Parent product is currently out of stock';
          const drawerContent = extendedModal.querySelector('.drawer__content');
          if (drawerContent && drawerContent.firstChild) {
            drawerContent.insertBefore(notice, drawerContent.firstChild);
          }
        }

        // Add notice in battery warranty modal
        const batteryModal = document.querySelector(`#battery-warranty-modal-${this.modalId}`);
        if (batteryModal) {
          const notice = document.createElement('div');
          notice.style.cssText = 'padding: 12px; margin: 16px; background: rgb(239, 68, 68, 0.1); border-radius: 8px; color: rgb(239, 68, 68); text-align: center; font-size: 14px; font-weight: 500;';
          notice.textContent = 'Parent product is currently out of stock';
          const drawerContent = batteryModal.querySelector('.drawer__content');
          if (drawerContent && drawerContent.firstChild) {
            drawerContent.insertBefore(notice, drawerContent.firstChild);
          }
        }
      }
    }

    /**
     * Check if parent product has stock available
     * Validates that parent product (ride-on) has at least one variant in stock
     * @returns {{available: boolean, message: string}}
     */
    checkParentStock() {
      if (!this.productData || !this.productData.variants) {
        console.warn('[WarrantyComparisonWidget] No parent product data available for stock check');
        return {
          available: true, // Don't block if data not available
          message: 'No parent product data available'
        };
      }

      const { variants, title } = this.productData;

      // Check if ANY variant has stock available
      const hasStockInAnyVariant = variants.some(variant => {
        // Check if variant is available
        if (variant.available === true) {
          return true;
        }

        // Check inventory policy - if 'continue', can sell when out of stock
        if (variant.inventory_policy === 'continue') {
          return true;
        }

        // Check if inventory management is disabled
        if (!variant.inventory_management || variant.inventory_management === null) {
          return true;
        }

        // Check if variant has inventory quantity > 0
        if (variant.inventory_quantity && variant.inventory_quantity > 0) {
          return true;
        }

        return false;
      });

      if (hasStockInAnyVariant) {
        return {
          available: true,
          message: `Parent product "${title}" has stock available`
        };
      } else {
        return {
          available: false,
          message: `Parent product "${title}" is out of stock`
        };
      }
    }

    /**
     * Get current bundle tier discount percentage
     * @returns {number} Discount as decimal (e.g., 0.15 for 15%)
     */
    getCurrentDiscount() {
      // Check if bundleAdapter exists and has current tier info
      if (window.bundleAdapter && window.bundleAdapter.currentTier) {
        const tierKey = window.bundleAdapter.currentTier;
        const tierData = this.bundleConfig?.tiers?.[tierKey];

        if (tierData && tierData.discount) {
          // Parse discount string like "15%" to 0.15
          const discountStr = tierData.discount.replace('%', '');
          return parseFloat(discountStr) / 100;
        }
      }

      // Default: no discount
      return 0;
    }

    /**
     * Calculate discounted price
     * @param {number} basePrice - Base price in cents
     * @returns {Object} { price: number, originalPrice: number, savings: number, discountPercent: number }
     */
    calculatePrice(basePrice) {
      const discount = this.getCurrentDiscount();
      const discountedPrice = Math.round(basePrice * (1 - discount));
      const savings = basePrice - discountedPrice;

      return {
        price: discountedPrice,
        originalPrice: basePrice,
        savings: savings,
        discountPercent: Math.round(discount * 100)
      };
    }

    /**
     * Render widget to container
     */
    render() {
      // Only show widget if product is eligible (ride-on)
      if (!this.isEligible) {
        this.hide();
        return;
      }

      // Build card + modal inline (x-modal pattern)
      const html = `
        ${this.buildCardHTML()}
        ${this.buildModalHTML()}
      `;

      // Use setContent to properly handle skeleton removal
      this.setContent(html);

      // Attach event listeners
      this.attachEventListeners();

      // Check parent stock and disable buttons if unavailable (async, don't block render)
      this.validateParentStockOnRender().catch(error => {
        console.error('[WarrantyComparisonWidget] Stock validation error:', error);
      });

      this.emit('warranty-comparison:rendered');
    }

    /**
     * Build widget card HTML
     */
    buildCardHTML() {
      const hasExtended = !!this.extendedWarrantyAddon;
      const hasBattery = !!this.batteryWarrantyAddon;
      const basePrice = hasExtended ? (this.extendedWarrantyAddon.actualPrice || this.extendedWarrantyAddon.tierValue * 100) : 0;
      const pricing = this.calculatePrice(basePrice);

      // Dark theme colors
      const bgColor = 'rgb(var(--color-drawer-text))';
      const textColor = 'rgb(var(--color-drawer-background))';
      const mutedColor = 'rgb(var(--color-drawer-background) / 0.7)';
      const cardBg = 'rgb(var(--color-drawer-background) / 0.1)';
      const cardBorder = 'rgb(var(--color-drawer-background) / 0.2)';

      return `
        <div class="pw-widget pw-warranty-comparison-widget" style="width: 100%;" data-button_hover="standard">
          <div
            class="pw-warranty-card"
            role="button"
            tabindex="0"
            aria-label="Click to learn more about warranty options"
            style="padding: 16px; background: ${bgColor}; border-radius: var(--rounded-card); cursor: pointer; transition: all 0.2s ease;">

            <!-- Header with Icon -->
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: ${cardBg}; border-radius: var(--rounded-button);">
                ${this.getShieldIconSVG('var(--color-drawer-background)')}
              </div>
              <div style="flex: 1;">
                <h3 class="pw-text-lg pw-font-bold" style="margin: 0; color: ${textColor};">Warranty Protection</h3>
                <p class="pw-text-sm" style="margin: 0; color: ${mutedColor};">
                  Compare warranty options
                </p>
              </div>
            </div>

            <!-- Quick Comparison -->
            <div style="display: grid; grid-template-columns: ${hasExtended || hasBattery ? '1fr 1fr' : '1fr'}; gap: 12px; margin-bottom: 16px;">

              <!-- Standard Warranty -->
              <div style="padding: 12px; background: ${cardBg}; border-radius: var(--rounded-card); border: 1px solid ${cardBorder};">
                <div style="display: flex; flex-direction: column; gap: 6px; text-align: center;">
                  <span style="font-size: 0.75rem; font-weight: 700; color: ${mutedColor}; text-transform: uppercase; letter-spacing: 0.05em;">Included</span>
                  <span style="font-size: 0.875rem; font-weight: 600; color: ${textColor};">Standard</span>
                  <span style="font-size: 1.25rem; font-weight: 700; color: ${textColor};">${this.warrantyData.standard.duration}</span>
                </div>
              </div>

              <!-- Extended Warranty (only if available) -->
              ${hasExtended || hasBattery ? `
                <div style="padding: 12px; background: ${cardBg}; border-radius: var(--rounded-card); border: 1px solid ${cardBorder};">
                  <div style="display: flex; flex-direction: column; gap: 6px; text-align: center;">
                    <span style="font-size: 0.75rem; font-weight: 700; color: ${mutedColor}; text-transform: uppercase; letter-spacing: 0.05em;">Available</span>
                    <span style="font-size: 0.875rem; font-weight: 600; color: ${textColor};">${hasExtended ? 'Extended' : 'Battery'}</span>
                    <span style="font-size: 1.25rem; font-weight: 700; color: ${textColor};">${this.warrantyData.extended.duration}</span>
                  </div>
                </div>
              ` : ''}

            </div>

            <!-- Extended Warranty Price (if available) -->
            ${hasExtended ? `
              <div style="padding: 12px; margin-bottom: 16px; background: ${cardBg}; border-radius: var(--rounded-card); border: 1px solid ${cardBorder};">
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;">
                  <span class="pw-text-sm" style="color: ${mutedColor};">Extended Warranty:</span>
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <span class="pw-text-lg pw-font-bold" style="color: ${textColor};">
                      ${this.manager.formatPrice(pricing.price)}
                    </span>
                    ${pricing.discountPercent > 0 ? `
                      <span style="text-decoration: line-through; color: ${mutedColor}; font-size: 0.875rem;">
                        ${this.manager.formatPrice(pricing.originalPrice)}
                      </span>
                    ` : ''}
                  </div>
                </div>
                ${pricing.discountPercent > 0 ? `
                  <div style="margin-top: 8px; display: flex; justify-content: flex-end;">
                    <div style="padding: var(--sp-1d5) var(--sp-3); background: rgb(var(--color-drawer-background)); border-radius: 6px;">
                      <span style="font-size: var(--text-sm); font-weight: 700; color: rgb(var(--color-drawer-text));">Save ${this.manager.formatPrice(pricing.savings)}</span>
                    </div>
                  </div>
                ` : ''}
              </div>
            ` : ''}

            <!-- Benefits List -->
            <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px;">
              ${this.warrantyData.standard.highlights.map(highlight => `
                <div style="display: flex; align-items: start; gap: 8px;">
                  <svg style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px; color: ${textColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="pw-text-sm" style="color: ${mutedColor};">
                    ${highlight}
                  </span>
                </div>
              `).join('')}
            </div>

            <!-- Learn More Button -->
            <div style="display: flex; justify-content: flex-end;">
              <button
                type="button"
                class="pw-learn-more"
                aria-controls="${this.modalId}"
                aria-expanded="false"
                style="color: ${mutedColor};"
              >
                <span>Learn more</span>
                <svg class="pw-learn-more__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>

          </div>
        </div>
      `;
    }

    /**
     * Build modal HTML using x-modal pattern
     */
    buildModalHTML() {
      const hasExtended = !!this.extendedWarrantyAddon;
      const basePrice = hasExtended ? (this.extendedWarrantyAddon.actualPrice || this.extendedWarrantyAddon.tierValue * 100) : 0;
      const pricing = this.calculatePrice(basePrice);

      return `
        <x-modal id="${this.modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="Warranty Comparison" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${this.modalId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">Warranty Comparison</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${this.modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Hero Section -->
                <div class="text-center">
                  <div style="display: inline-flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; background: rgb(var(--color-base-button)); border-radius: 50%; margin-bottom: 1rem;">
                    ${this.getShieldIconSVG('255, 255, 255')}
                  </div>
                  <h3 class="heading font-bold text-xl md:text-2xl" style="margin-bottom: 0.75rem; color: rgb(var(--color-drawer-text));">
                    Protect Your Investment
                  </h3>
                  <p class="text-base" style="max-width: 500px; margin: 0 auto; color: rgb(var(--color-drawer-text) / 0.8);">
                    Compare our warranty options and choose the coverage that's right for you
                  </p>
                </div>

                <!-- Comparison Table -->
                ${this.buildComparisonTableHTML()}

                <!-- Standard Warranty Terms -->
                ${this.buildStandardWarrantyTermsHTML()}

                <!-- Warranty Addon Options -->
                ${this.buildWarrantyAddonsHTML()}

                <!-- Competitor Comparison Section -->
                ${this.buildCompetitorComparisonHTML()}

                <!-- Important Notice -->
                <div style="padding: 1rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card);">
                  <div style="display: flex; align-items: start; gap: 0.75rem;">
                    ${this.getInfoIconSVG()}
                    <div style="flex: 1;">
                      <p class="text-sm" style="margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                        All warranties are subject to terms and conditions. Claims must be reported within the warranty period with proof of purchase.
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </x-modal>
      `;
    }

    /**
     * Build comparison table HTML
     */
    buildComparisonTableHTML() {
      const hasExtended = !!this.extendedWarrantyAddon;
      const hasBattery = !!this.batteryWarrantyAddon;

      // Only show comparison table if extended warranty is available
      // Battery addon alone doesn't need comparison table (confusing to show "Not available" for everything)
      if (!hasExtended) {
        return `
          <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
            <h4 class="font-bold text-base" style="margin-bottom: 1.25rem; color: rgb(var(--color-drawer-text));">Your Standard Warranty:</h4>

            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem;">
              ${this.comparisonRows.map(row => `
                <div style="padding: 0.75rem; background: rgb(var(--color-drawer-text) / 0.02); border-radius: var(--rounded-card); ${row.highlight ? 'border: 2px solid rgb(var(--color-base-button) / 0.2);' : 'border: 1px solid rgb(var(--color-drawer-text) / 0.08);'}">
                  <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
                    ${this.getIconForType(row.icon, '16px')}
                    <span style="font-size: 0.875rem; font-weight: 600; color: rgb(var(--color-drawer-text));">${row.label}</span>
                  </div>
                  <p style="margin: 0; font-size: 0.875rem; color: rgb(var(--color-drawer-text) / 0.85); padding-left: 24px;">
                    ${row.standard}
                  </p>
                </div>
              `).join('')}
            </div>
          </div>
        `;
      }

      // Show comparison table if extended warranty is available
      return `
        <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
          <h4 class="font-bold text-base" style="margin-bottom: 1.25rem; color: rgb(var(--color-drawer-text));">Coverage Comparison:</h4>

          <!-- Desktop Table -->
          <div style="display: none; overflow-x: auto;" class="sm:block">
            <table style="width: 100%; border-collapse: collapse; font-size: 0.875rem;">
              <thead>
                <tr style="border-bottom: 2px solid rgb(var(--color-drawer-text) / 0.1);">
                  <th style="padding: 12px; text-align: left; font-weight: 600; color: rgb(var(--color-drawer-text));">Feature</th>
                  <th style="padding: 12px; text-align: center; font-weight: 600; color: rgb(var(--color-drawer-text)); background: rgb(var(--color-drawer-text) / 0.02);">
                    <div style="display: flex; flex-direction: column; gap: 4px; align-items: center;">
                      <span style="font-size: 0.75rem; font-weight: 700; color: rgb(34, 197, 94); text-transform: uppercase;">Included</span>
                      <span>Standard ${this.warrantyData.standard.duration}</span>
                    </div>
                  </th>
                  <th style="padding: 12px; text-align: center; font-weight: 600; color: rgb(var(--color-drawer-text)); background: rgb(59, 130, 246, 0.05);">
                    <div style="display: flex; flex-direction: column; gap: 4px; align-items: center;">
                      <span style="font-size: 0.75rem; font-weight: 700; color: rgb(59, 130, 246); text-transform: uppercase;">Available</span>
                      <span>${hasExtended ? 'Extended' : 'Battery'} ${this.warrantyData.extended.duration}</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                ${this.comparisonRows.map((row, index) => `
                  <tr style="border-bottom: 1px solid rgb(var(--color-drawer-text) / 0.08); ${row.highlight ? 'background: rgb(var(--color-base-button) / 0.03);' : ''}">
                    <td style="padding: 12px; color: rgb(var(--color-drawer-text) / 0.9); font-weight: ${row.highlight ? '600' : '400'};">
                      <div style="display: flex; align-items: center; gap: 8px;">
                        ${this.getIconForType(row.icon)}
                        <span>${row.label}</span>
                      </div>
                    </td>
                    <td style="padding: 12px; text-align: center; color: rgb(var(--color-drawer-text) / 0.85); background: rgb(var(--color-drawer-text) / 0.02);">
                      ${row.standard}
                    </td>
                    <td style="padding: 12px; text-align: center; color: rgb(var(--color-drawer-text) / 0.85); background: rgb(59, 130, 246, 0.05); font-weight: ${row.highlight ? '600' : '400'};">
                      ${row.extended}
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <!-- Mobile Stacked Cards -->
          <div style="display: flex; flex-direction: column; gap: 1rem;" class="sm:hidden">

            <!-- Standard Warranty Card -->
            <div style="padding: 1rem; background: rgb(var(--color-drawer-text) / 0.02); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.1);">
              <div style="text-align: center; margin-bottom: 1rem; padding-bottom: 0.75rem; border-bottom: 2px solid rgb(var(--color-drawer-text) / 0.1);">
                <span style="display: block; font-size: 0.75rem; font-weight: 700; color: rgb(34, 197, 94); text-transform: uppercase; margin-bottom: 4px;">Included</span>
                <span style="font-size: 1rem; font-weight: 600; color: rgb(var(--color-drawer-text));">Standard ${this.warrantyData.standard.duration}</span>
              </div>
              ${this.comparisonRows.map(row => `
                <div style="display: flex; justify-content: space-between; align-items: start; gap: 12px; padding: 8px 0; border-bottom: 1px solid rgb(var(--color-drawer-text) / 0.05);">
                  <div style="display: flex; align-items: center; gap: 6px; flex: 1;">
                    ${this.getIconForType(row.icon, '14px')}
                    <span style="font-size: 0.8rem; color: rgb(var(--color-drawer-text) / 0.7);">${row.label}:</span>
                  </div>
                  <span style="font-size: 0.8rem; font-weight: 500; color: rgb(var(--color-drawer-text)); text-align: right; flex: 1;">${row.standard}</span>
                </div>
              `).join('')}
            </div>

            <!-- Extended Warranty Card -->
            <div style="padding: 1rem; background: rgb(59, 130, 246, 0.05); border-radius: var(--rounded-card); border: 2px solid rgb(59, 130, 246, 0.2);">
              <div style="text-align: center; margin-bottom: 1rem; padding-bottom: 0.75rem; border-bottom: 2px solid rgb(59, 130, 246, 0.2);">
                <span style="display: block; font-size: 0.75rem; font-weight: 700; color: rgb(59, 130, 246); text-transform: uppercase; margin-bottom: 4px;">Available</span>
                <span style="font-size: 1rem; font-weight: 600; color: rgb(var(--color-drawer-text));">${hasExtended ? 'Extended' : 'Battery'} ${this.warrantyData.extended.duration}</span>
              </div>
              ${this.comparisonRows.map(row => `
                <div style="display: flex; justify-content: space-between; align-items: start; gap: 12px; padding: 8px 0; border-bottom: 1px solid rgb(59, 130, 246, 0.1);">
                  <div style="display: flex; align-items: center; gap: 6px; flex: 1;">
                    ${this.getIconForType(row.icon, '14px')}
                    <span style="font-size: 0.8rem; color: rgb(var(--color-drawer-text) / 0.7);">${row.label}:</span>
                  </div>
                  <span style="font-size: 0.8rem; font-weight: ${row.highlight ? '600' : '500'}; color: rgb(var(--color-drawer-text)); text-align: right; flex: 1;">${row.extended}</span>
                </div>
              `).join('')}
            </div>

          </div>
        </div>
      `;
    }

    /**
     * Build standard warranty terms section
     */
    buildStandardWarrantyTermsHTML() {
      const { powerType, hasBattery, isRiiRoo, isOneMoto } = this.productAttributes;

      // Build dynamic "What's Covered" list based on vendor and power type
      const coveredItems = [];

      if (powerType === 'battery') {
        if (isRiiRoo) {
          coveredItems.push('Drive motors');
          coveredItems.push('Steering motors');
          coveredItems.push('Speed controllers / motherboards');
          coveredItems.push('Wiring looms & connectors');
          coveredItems.push('Chargers & charging ports');
          coveredItems.push('Media displays & sound units');
          coveredItems.push('Parental remote controls');
          coveredItems.push('All operational switches (On/Off, Light, Forward/Reverse, High/Low speed, 2x2/4x4 drive mode selector)');
        } else if (isOneMoto) {
          coveredItems.push('Motors');
          coveredItems.push('Speed controllers');
          coveredItems.push('Motor controller / motherboard');
          coveredItems.push('Throttle assembly');
          coveredItems.push('Wiring looms & electrical connectors');
          coveredItems.push('Chargers & charging ports');
          coveredItems.push('Manufacturing defects');
          coveredItems.push('Structural frame & weld failures');
          coveredItems.push('Parts sent out first, then return for repair if needed');
        } else {
          coveredItems.push('Manufacturing defects and faulty components');
          coveredItems.push('Motor, electronics, and steering system issues');
        }
      } else if (powerType === 'petrol') {
        if (isOneMoto) {
          coveredItems.push('<strong>30-Day Full Warranty Covers:</strong>');
          coveredItems.push('Manufacturing defects');
          coveredItems.push('Faulty mechanical components');
          coveredItems.push('Engine starting issues (non-maintenance related)');
          coveredItems.push('Electrical faults');
          coveredItems.push('Faulty carburettor, pull-start, ignition coil, CDI, etc.');
          coveredItems.push('Any defective part that prevents normal operation');
          coveredItems.push('<strong>12-Month Structural Warranty Covers:</strong>');
          coveredItems.push('Frame weld failures');
          coveredItems.push('De-welded joints');
          coveredItems.push('Structural metal cracking (not caused by impact or misuse)');
          coveredItems.push('Exhaust brackets/metal components detaching due to faulty welding');
        } else {
          coveredItems.push('30-day manufacturer defect warranty only');
          coveredItems.push('Parts supply or repair at cost after 30 days');
        }
      } else {
        coveredItems.push('Manufacturing defects and faulty components');
        coveredItems.push('Mechanical components and moving parts');
        coveredItems.push('Structural issues and workmanship defects');
      }

      return `
        <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
          <h4 class="font-bold text-base" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text));">Standard Warranty Terms:</h4>

          <div style="display: flex; flex-direction: column; gap: 1rem;">

            ${isOneMoto && powerType === 'petrol' ? `
              <!-- Coverage Period -->
              <div>
                <h5 class="font-bold text-sm" style="margin-bottom: 0.5rem; color: rgb(var(--color-base-button)); display: flex; align-items: center; gap: 6px;">
                  ${this.getCheckIconSVG()}
                  Coverage Period:
                </h5>
                <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    30 days full warranty on all parts & components
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    12 months structural/frame warranty only
                  </li>
                </ul>
              </div>
            ` : ''}

            <!-- What's Covered -->
            <div>
              <h5 class="font-bold text-sm" style="margin-bottom: 0.5rem; color: rgb(var(--color-base-button)); display: flex; align-items: center; gap: 6px;">
                ${this.getCheckIconSVG()}
                What's Covered:
              </h5>
              <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                ${coveredItems.map(item => `
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    ${item}
                  </li>
                `).join('')}
              </ul>
            </div>

            ${isRiiRoo && powerType === 'battery' ? `
              <!-- Battery (Not Covered) -->
              <div>
                <h5 class="font-bold text-sm" style="margin-bottom: 0.5rem; color: rgb(239, 68, 68); display: flex; align-items: center; gap: 6px;">
                  ${this.getXIconSVG()}
                  Battery:
                </h5>
                <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Not covered under the 6-month plan
                  </li>
                </ul>
              </div>

              <!-- Consumables (Not Covered) -->
              <div>
                <h5 class="font-bold text-sm" style="margin-bottom: 0.5rem; color: rgb(239, 68, 68); display: flex; align-items: center; gap: 6px;">
                  ${this.getXIconSVG()}
                  Consumables (Not Covered):
                </h5>
                <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Wheels & tyres
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Seats
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Lights & bulbs
                  </li>
                </ul>
              </div>

              <!-- What's NOT Covered -->
              <div>
                <h5 class="font-bold text-sm" style="margin-bottom: 0.5rem; color: rgb(239, 68, 68); display: flex; align-items: center; gap: 6px;">
                  ${this.getXIconSVG()}
                  What's NOT Covered:
                </h5>
                <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Misuse
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Water damage
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Incorrect assembly
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Wear & tear
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Overloading
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Modifications / DIY repairs
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Negligence
                  </li>
                </ul>
              </div>
            ` : isOneMoto && powerType === 'battery' ? `
              <!-- Battery (Not Covered) -->
              <div>
                <h5 class="font-bold text-sm" style="margin-bottom: 0.5rem; color: rgb(239, 68, 68); display: flex; align-items: center; gap: 6px;">
                  ${this.getXIconSVG()}
                  Battery:
                </h5>
                <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Battery not covered under warranty
                  </li>
                </ul>
              </div>

              <!-- Consumables (Not Covered) -->
              <div>
                <h5 class="font-bold text-sm" style="margin-bottom: 0.5rem; color: rgb(239, 68, 68); display: flex; align-items: center; gap: 6px;">
                  ${this.getXIconSVG()}
                  Consumables (Not Covered):
                </h5>
                <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Wheels & tyres
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Seats, handlebars, grips
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Lights & bulbs
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Chains & sprockets
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Brake pads, brake discs, brake cables
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Suspension bushings
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    USB & Aux-in ports (if applicable)
                  </li>
                </ul>
              </div>

              <!-- What's NOT Covered -->
              <div>
                <h5 class="font-bold text-sm" style="margin-bottom: 0.5rem; color: rgb(239, 68, 68); display: flex; align-items: center; gap: 6px;">
                  ${this.getXIconSVG()}
                  What's NOT Covered:
                </h5>
                <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Misuse or incorrect assembly
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Water damage or damage from cold/damp storage
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Overloading beyond specified weight limits
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Lack of maintenance (chain tension, lubrication, brake service)
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Unauthorised modifications or repairs
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Damage caused by impacts, crashes, or off-road abuse
                  </li>
                </ul>
              </div>
            ` : isOneMoto && powerType === 'petrol' ? `
              <!-- After 30 Days -->
              <div>
                <h5 class="font-bold text-sm" style="margin-bottom: 0.5rem; color: rgb(239, 68, 68); display: flex; align-items: center; gap: 6px;">
                  ${this.getXIconSVG()}
                  After 30 Days:
                </h5>
                <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Customer is responsible for all engine maintenance and wear-related repairs
                  </li>
                </ul>
              </div>

              <!-- Consumables (Not Covered / No Warranty After 30 Days) -->
              <div>
                <h5 class="font-bold text-sm" style="margin-bottom: 0.5rem; color: rgb(239, 68, 68); display: flex; align-items: center; gap: 6px;">
                  ${this.getXIconSVG()}
                  Consumables (Not Covered / No Warranty After 30 Days):
                </h5>
                <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Wheels & tyres
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Seats, handlebars, grips
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Chains, sprockets, spark plugs
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Brake pads, brake discs, brake lines
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Clutches, pull-start ropes
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Lights & bulbs
                  </li>
                </ul>
              </div>

              <!-- What's NOT Covered -->
              <div>
                <h5 class="font-bold text-sm" style="margin-bottom: 0.5rem; color: rgb(239, 68, 68); display: flex; align-items: center; gap: 6px;">
                  ${this.getXIconSVG()}
                  What's NOT Covered:
                </h5>
                <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Incorrect fuel/oil mixture use
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Lack of maintenance (oil changes, spark plug care, carb cleaning)
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Overheating due to misuse
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Off-road abuse or stunt damage
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Water damage
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Storing in cold/damp environments
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Overloading beyond weight limits
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Engine modifications or tuning
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Unauthorised repairs or aftermarket parts
                  </li>
                </ul>
              </div>
            ` : `
              <!-- What's NOT Covered (Default) -->
              <div>
                <h5 class="font-bold text-sm" style="margin-bottom: 0.5rem; color: rgb(239, 68, 68); display: flex; align-items: center; gap: 6px;">
                  ${this.getXIconSVG()}
                  What's NOT Covered:
                </h5>
                <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Misuse, incorrect assembly, or deliberate damage
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Water damage or damage from cold/damp storage
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Overloading beyond specified weight limit
                  </li>
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">•</span>
                    Unauthorised modifications or repairs
                  </li>
                </ul>
              </div>
            `}

            <!-- Claims Process -->
            <div>
              <h5 class="font-bold text-sm" style="margin-bottom: 0.5rem; color: rgb(var(--color-base-button)); display: flex; align-items: center; gap: 6px;">
                ${this.getInfoIconSVG()}
                Claims Process:
              </h5>
              <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                  <span style="position: absolute; left: 0;">1.</span>
                  Contact ${isOneMoto ? 'OneMoto' : 'RiiRoo'} customer service team
                </li>
                <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                  <span style="position: absolute; left: 0;">2.</span>
                  Team will assess eligibility and repair difficulty
                </li>
                <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                  <span style="position: absolute; left: 0;">3.</span>
                  Most claims resolved by sending replacement parts
                </li>
                <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                  <span style="position: absolute; left: 0;">4.</span>
                  If parts don't resolve issue, arrange return for repair
                </li>
                ${isRiiRoo ? `
                  <li style="padding: 0.25rem 0; padding-left: 1.5rem; position: relative;">
                    <span style="position: absolute; left: 0;">5.</span>
                    If unrepairable, replace with refurbished model
                  </li>
                ` : ''}
              </ul>
            </div>

          </div>
        </div>
      `;
    }

    /**
     * Build warranty addons section - shows both extended and battery warranties
     */
    buildWarrantyAddonsHTML() {
      const sections = [];

      // Extended Warranty Section
      if (this.extendedWarrantyAddon) {
        const basePrice = this.extendedWarrantyAddon.actualPrice || this.extendedWarrantyAddon.tierValue * 100;
        const pricing = this.calculatePrice(basePrice);
        sections.push(this.buildExtendedWarrantyCTAHTML(pricing));
      }

      // Battery Warranty Section
      if (this.batteryWarrantyAddon) {
        const basePrice = this.batteryWarrantyAddon.actualPrice || this.batteryWarrantyAddon.tierValue * 100;
        const pricing = this.calculatePrice(basePrice);
        sections.push(this.buildBatteryWarrantyCTAHTML(pricing));
      }

      return sections.join('');
    }

    /**
     * Build extended warranty CTA section with add to cart
     */
    buildExtendedWarrantyCTAHTML(pricing) {
      return `
        <div style="padding: 1.5rem; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-card); border: 2px solid rgb(var(--color-base-button) / 0.3);">
          <h4 class="font-bold text-lg" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text));">
            Upgrade to Extended Warranty
          </h4>

          <p class="text-sm" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text) / 0.85);">
            ${this.extendedWarrantyAddon.description}
          </p>

          <!-- Extended Warranty Benefits -->
          <div style="margin-bottom: 1.25rem;">
            <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
              ${this.warrantyData.extended.highlights.map(highlight => `
                <li style="padding: 0.375rem 0; display: flex; align-items: start; gap: 0.5rem;">
                  ${this.getCheckIconSVG()}
                  <span>${highlight}</span>
                </li>
              `).join('')}
            </ul>
          </div>

          <!-- Pricing -->
          <div style="display: flex; flex-direction: column; gap: 0.75rem; align-items: center; margin-bottom: 1rem;">
            <div style="display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; justify-content: center;">
              <span style="font-size: 1.75rem; font-weight: 700; color: rgb(var(--color-base-button));">
                ${this.manager.formatPrice(pricing.price)}
              </span>
              ${pricing.discountPercent > 0 ? `
                <span style="text-decoration: line-through; color: rgb(var(--color-drawer-text) / 0.5); font-size: 1.25rem;">
                  ${this.manager.formatPrice(pricing.originalPrice)}
                </span>
              ` : ''}
            </div>
            ${pricing.discountPercent > 0 ? `
              <div style="display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; background: rgba(0, 0, 0, 0.1); border-radius: var(--rounded-button);">
                ${this.getSavingsIconSVG()}
                <span style="font-size: 1rem; font-weight: 600; color: rgb(var(--color-drawer-text));">
                  Save ${this.manager.formatPrice(pricing.savings)} (${pricing.discountPercent}% off)
                </span>
              </div>
            ` : ''}
          </div>

          <!-- Add to Cart Button -->
          <button
            id="add-warranty-btn-${this.modalId}"
            type="button"
            ${!this.extendedVariantId ? 'disabled' : ''}
            style="width: 100%; background: rgb(var(--color-base-button)); color: rgb(var(--color-base-button-text)); border: none; padding: 1rem; border-radius: var(--rounded-button); font-size: 1.125rem; font-weight: 700; cursor: pointer; transition: all 0.2s ease; ${!this.extendedVariantId ? 'opacity: 0.5; cursor: not-allowed;' : ''}"
            onmouseover="${this.extendedVariantId ? 'this.style.opacity=\'0.9\'' : ''}"
            onmouseout="${this.extendedVariantId ? 'this.style.opacity=\'1\'' : ''}">
            ${this.extendedVariantId ? 'Add Extended Warranty to Cart' : 'Warranty Unavailable'}
          </button>

          <!-- Status Message Container -->
          <div id="warranty-status-${this.modalId}" style="margin-top: 1rem; text-align: center; display: none;"></div>
        </div>
      `;
    }

    /**
     * Build battery warranty CTA section with add to cart
     */
    buildBatteryWarrantyCTAHTML(pricing) {
      return `
        <div style="padding: 1.5rem; background: rgb(20, 184, 166, 0.1); border-radius: var(--rounded-card); border: 2px solid rgb(20, 184, 166, 0.3); margin-top: 1rem;">
          <h4 class="font-bold text-lg" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text));">
            Battery Warranty Extension
          </h4>

          <p class="text-sm" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text) / 0.85);">
            ${this.batteryWarrantyAddon.description || 'Extend your battery warranty to 12 months for complete peace of mind.'}
          </p>

          <!-- Battery Warranty Benefits -->
          <div style="margin-bottom: 1.25rem;">
            <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
              <li style="padding: 0.375rem 0; display: flex; align-items: start; gap: 0.5rem;">
                ${this.getCheckIconSVG()}
                <span>12 months battery coverage</span>
              </li>
              <li style="padding: 0.375rem 0; display: flex; align-items: start; gap: 0.5rem;">
                ${this.getCheckIconSVG()}
                <span>Perfect if you only need battery protection</span>
              </li>
              <li style="padding: 0.375rem 0; display: flex; align-items: start; gap: 0.5rem;">
                ${this.getCheckIconSVG()}
                <span>More affordable than full extended warranty</span>
              </li>
            </ul>
          </div>

          <!-- Pricing -->
          <div style="display: flex; flex-direction: column; gap: 0.75rem; align-items: center; margin-bottom: 1rem;">
            <div style="display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; justify-content: center;">
              <span style="font-size: 1.75rem; font-weight: 700; color: rgb(20, 184, 166);">
                ${this.manager.formatPrice(pricing.price)}
              </span>
              ${pricing.discountPercent > 0 ? `
                <span style="text-decoration: line-through; color: rgb(var(--color-drawer-text) / 0.5); font-size: 1.25rem;">
                  ${this.manager.formatPrice(pricing.originalPrice)}
                </span>
              ` : ''}
            </div>
            ${pricing.discountPercent > 0 ? `
              <div style="display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; background: rgba(0, 0, 0, 0.1); border-radius: var(--rounded-button);">
                ${this.getSavingsIconSVG()}
                <span style="font-size: 1rem; font-weight: 600; color: rgb(var(--color-drawer-text));">
                  Save ${this.manager.formatPrice(pricing.savings)} (${pricing.discountPercent}% off)
                </span>
              </div>
            ` : ''}
          </div>

          <!-- Add to Cart Button -->
          <button
            id="add-battery-warranty-btn-${this.modalId}"
            type="button"
            ${!this.batteryVariantId ? 'disabled' : ''}
            style="width: 100%; background: rgb(20, 184, 166); color: white; border: none; padding: 1rem; border-radius: var(--rounded-button); font-size: 1.125rem; font-weight: 700; cursor: pointer; transition: all 0.2s ease; ${!this.batteryVariantId ? 'opacity: 0.5; cursor: not-allowed;' : ''}"
            onmouseover="${this.batteryVariantId ? 'this.style.opacity=\'0.9\'' : ''}"
            onmouseout="${this.batteryVariantId ? 'this.style.opacity=\'1\'' : ''}">
            ${this.batteryVariantId ? 'Add Battery Warranty to Cart' : 'Warranty Unavailable'}
          </button>

          <!-- Status Message Container -->
          <div id="battery-warranty-status-${this.modalId}" style="margin-top: 1rem; text-align: center; display: none;"></div>
        </div>
      `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
      // Add to cart button for extended warranty
      const addButton = document.getElementById(`add-warranty-btn-${this.modalId}`);
      if (addButton && this.extendedVariantId) {
        addButton.addEventListener('click', () => {
          this.addExtendedWarrantyToCart();
        });
      }

      // Add to cart button for battery warranty
      const addBatteryButton = document.getElementById(`add-battery-warranty-btn-${this.modalId}`);
      if (addBatteryButton && this.batteryVariantId) {
        addBatteryButton.addEventListener('click', () => {
          this.addBatteryWarrantyToCart();
        });
      }

      // Make warranty card clickable to open modal
      const card = this.container.querySelector('.pw-warranty-card');
      if (card) {
        // Click event
        card.addEventListener('click', (e) => {
          // Don't trigger if clicking the learn more button
          if (e.target.closest('.pw-learn-more')) {
            return;
          }
          const modal = document.getElementById(this.modalId);
          if (modal && !modal.open) {
            modal.show();
          }
        });

        // Keyboard event (Enter or Space)
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            card.click();
          }
        });

        // Hover effect - add subtle grey border
        card.addEventListener('mouseenter', () => {
          card.style.outline = '2px solid rgb(var(--color-drawer-background) / 0.4)';
          card.style.outlineOffset = '-2px';
        });

        card.addEventListener('mouseleave', () => {
          card.style.outline = 'none';
        });
      }
    }

    /**
     * Add extended warranty to cart
     */
    async addExtendedWarrantyToCart() {
      if (!this.extendedVariantId) {
        console.warn('[WarrantyComparisonWidget] No variant ID available');
        return;
      }

      const button = document.getElementById(`add-warranty-btn-${this.modalId}`);
      const statusContainer = document.getElementById(`warranty-status-${this.modalId}`);

      // Ensure ProductLinkingManager is loaded
      if (!window.ProductLinkingManager) {
        console.error('[WarrantyComparisonWidget] ProductLinkingManager not loaded');
        alert('Unable to add warranty. Please refresh the page and try again.');
        return;
      }

      // CHECK PARENT STOCK FIRST - Don't allow addon if parent is out of stock
      const parentStockCheck = this.checkParentStock();

      if (!parentStockCheck.available) {
        console.warn('[WarrantyComparisonWidget] Parent product out of stock:', parentStockCheck.message);

        // Show error message
        if (statusContainer) {
          statusContainer.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px; background: rgb(239, 68, 68, 0.1); border-radius: var(--rounded-button); color: rgb(239, 68, 68);">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/><path d="M13 7L7 13M7 7l6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              <span class="text-sm font-bold">Parent product is out of stock</span>
            </div>
          `;
          statusContainer.style.display = 'block';
        }

        // Disable button permanently
        if (button) {
          button.disabled = true;
          button.textContent = 'Parent Out of Stock';
        }

        return;
      }

      // Ensure PARENT product is in cart first
      const linkingManager = new ProductLinkingManager({
        productId: this.productData.id,
        productHandle: this.productData.handle,
        productVariantId: null, // Let user select variant if needed

        onReady: async (productLinkId, cartItem) => {

          // Disable button and show loading state
          if (button) {
            button.disabled = true;
            button.textContent = 'Adding to Cart...';
          }

          try {
            // Add extended warranty with productLinkId linking (simple JSON like bundle drawer)
            // Get tier attributes BEFORE adding (bundle drawer pattern)
            let tierAttrs = {
              _bundleTier: 'basic',
              _bundle: 'basic',
              _bundleItemCount: '1'
            };

            if (window.ProductLinkingManager && this.extendedWarrantyAddon && this.extendedWarrantyAddon.actualPrice) {
              const tempManager = new ProductLinkingManager({ productId: null, productHandle: null });
              const addonPrice = this.extendedWarrantyAddon.actualPrice / 100; // Convert from pence to pounds
              tierAttrs = await tempManager.getTierAttributesForNewAddon(productLinkId, addonPrice);
            }

            // Add warranty with tier attributes already set (bundle drawer pattern)
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                id: this.extendedVariantId,
                quantity: 1,
                properties: {
                  productLinkId: productLinkId,
                  _bundleComponent: 'true',
                  ...tierAttrs
                }
              })
            });

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();

            // Update existing addons to match the new tier/count (bundle drawer pattern)
            if (window.ProductLinkingManager) {
              const tempManager = new ProductLinkingManager({ productId: null, productHandle: null });
              await tempManager.recalculateBundleTier(productLinkId);
            }

            // Refresh cart with sections (bundle drawer pattern)
            const sectionsToUpdate = [];
            const cartDrawerElement = document.querySelector('cart-drawer');
            if (cartDrawerElement) {
              sectionsToUpdate.push(window.theme.utils.sectionId(cartDrawerElement));
            }
            const cartPageElement = document.querySelector('.cart-page');
            if (cartPageElement) {
              sectionsToUpdate.push(window.theme.utils.sectionId(cartPageElement));
            }

            const updatedCartResponse = await fetch(window.theme.routes.cart_update_url, {
              ...window.theme.utils.fetchConfig(),
              method: 'POST',
              body: JSON.stringify({ sections: sectionsToUpdate })
            });

            const updatedState = await updatedCartResponse.json();

            // Publish cart update
            if (window.theme && window.theme.pubsub) {
              window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                cart: updatedState
              });
            }

            // Show success message
            if (statusContainer) {
              statusContainer.innerHTML = `
                <div style="display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px; background: rgb(34, 197, 94, 0.1); border-radius: var(--rounded-button); color: rgb(34, 197, 94);">
                  ${this.getCheckCircleIconSVG()}
                  <span class="text-sm font-bold">Extended warranty added to cart!</span>
                </div>
              `;
              statusContainer.style.display = 'block';
            }

            // Emit event
            this.emit('warranty:added-to-cart', {
              variantId: this.extendedVariantId,
              product: this.extendedWarrantyAddon,
              productLinkId: productLinkId
            });

            // Update button text
            if (button) {
              button.textContent = 'Added to Cart!';
              setTimeout(() => {
                button.disabled = false;
                button.textContent = 'Add Extended Warranty to Cart';
                if (statusContainer) {
                  statusContainer.style.display = 'none';
                }
              }, 3000);
            }
          } catch (error) {
            console.error('[WarrantyComparisonWidget] Failed to add to cart:', error);
            // Show error message
            if (statusContainer) {
              statusContainer.innerHTML = `
                <div style="display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px; background: rgb(239, 68, 68, 0.1); border-radius: var(--rounded-button); color: rgb(239, 68, 68);">
                  ${this.getXCircleIconSVG()}
                  <span class="text-sm font-bold">Failed to add to cart. Please try again.</span>
                </div>
              `;
              statusContainer.style.display = 'block';
            }
            if (button) {
              button.disabled = false;
              button.textContent = 'Add Extended Warranty to Cart';
            }
          }
        },

        onCancel: () => {
          // Logging removed
        },

        onError: (error) => {
          console.error('[WarrantyComparisonWidget] Error ensuring parent in cart:', error);
          alert('Failed to add warranty. Please try again.');
        }
      });

      linkingManager.ensureParentInCart();
    }

    /**
     * Add battery warranty to cart
     */
    async addBatteryWarrantyToCart() {
      if (!this.batteryVariantId) {
        console.warn('[WarrantyComparisonWidget] No battery variant ID available');
        return;
      }

      const button = document.getElementById(`add-battery-warranty-btn-${this.modalId}`);
      const statusContainer = document.getElementById(`battery-warranty-status-${this.modalId}`);

      // Ensure ProductLinkingManager is loaded
      if (!window.ProductLinkingManager) {
        console.error('[WarrantyComparisonWidget] ProductLinkingManager not loaded');
        alert('Unable to add warranty. Please refresh the page and try again.');
        return;
      }

      // CHECK PARENT STOCK FIRST - Don't allow addon if parent is out of stock
      const parentStockCheck = this.checkParentStock();

      if (!parentStockCheck.available) {
        console.warn('[WarrantyComparisonWidget] Parent product out of stock:', parentStockCheck.message);

        // Show error message
        if (statusContainer) {
          statusContainer.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px; background: rgb(239, 68, 68, 0.1); border-radius: var(--rounded-button); color: rgb(239, 68, 68);">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/><path d="M13 7L7 13M7 7l6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              <span class="text-sm font-bold">Parent product is out of stock</span>
            </div>
          `;
          statusContainer.style.display = 'block';
        }

        // Disable button permanently
        if (button) {
          button.disabled = true;
          button.textContent = 'Parent Out of Stock';
        }

        return;
      }

      // Ensure PARENT product is in cart first
      const linkingManager = new ProductLinkingManager({
        productId: this.productData.id,
        productHandle: this.productData.handle,
        productVariantId: null, // Let user select variant if needed

        onReady: async (productLinkId, cartItem) => {

          // Disable button and show loading state
          if (button) {
            button.disabled = true;
            button.textContent = 'Adding to Cart...';
          }

          try {
            // Get tier attributes BEFORE adding (bundle drawer pattern)
            let tierAttrs = {
              _bundleTier: 'basic',
              _bundle: 'basic',
              _bundleItemCount: '1'
            };

            if (window.ProductLinkingManager && this.batteryWarrantyAddon && this.batteryWarrantyAddon.actualPrice) {
              const tempManager = new ProductLinkingManager({ productId: null, productHandle: null });
              const addonPrice = this.batteryWarrantyAddon.actualPrice / 100; // Convert from pence to pounds
              tierAttrs = await tempManager.getTierAttributesForNewAddon(productLinkId, addonPrice);
            }

            // Add battery warranty with tier attributes already set (bundle drawer pattern)
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                id: this.batteryVariantId,
                quantity: 1,
                properties: {
                  productLinkId: productLinkId,
                  _bundleComponent: 'true',
                  ...tierAttrs
                }
              })
            });

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();

            // Update existing addons to match the new tier/count (bundle drawer pattern)
            if (window.ProductLinkingManager) {
              const tempManager = new ProductLinkingManager({ productId: null, productHandle: null });
              await tempManager.recalculateBundleTier(productLinkId);
            }

            // Refresh cart with sections (bundle drawer pattern)
            const sectionsToUpdate = [];
            const cartDrawerElement = document.querySelector('cart-drawer');
            if (cartDrawerElement) {
              sectionsToUpdate.push(window.theme.utils.sectionId(cartDrawerElement));
            }
            const cartPageElement = document.querySelector('.cart-page');
            if (cartPageElement) {
              sectionsToUpdate.push(window.theme.utils.sectionId(cartPageElement));
            }

            const updatedCartResponse = await fetch(window.theme.routes.cart_update_url, {
              ...window.theme.utils.fetchConfig(),
              method: 'POST',
              body: JSON.stringify({ sections: sectionsToUpdate })
            });

            const updatedState = await updatedCartResponse.json();

            // Publish cart update
            if (window.theme && window.theme.pubsub) {
              window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                cart: updatedState
              });
            }

            // Show success message
            if (statusContainer) {
              statusContainer.innerHTML = `
                <div style="display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px; background: rgb(34, 197, 94, 0.1); border-radius: var(--rounded-button); color: rgb(34, 197, 94);">
                  ${this.getCheckCircleIconSVG()}
                  <span class="text-sm font-bold">Battery warranty added to cart!</span>
                </div>
              `;
              statusContainer.style.display = 'block';
            }

            // Emit event
            this.emit('warranty:added-to-cart', {
              variantId: this.batteryVariantId,
              product: this.batteryWarrantyAddon,
              productLinkId: productLinkId
            });

            // Update button text
            if (button) {
              button.textContent = 'Added to Cart!';
              setTimeout(() => {
                button.disabled = false;
                button.textContent = 'Add Battery Warranty to Cart';
                if (statusContainer) {
                  statusContainer.style.display = 'none';
                }
              }, 3000);
            }
          } catch (error) {
            console.error('[WarrantyComparisonWidget] Failed to add battery warranty to cart:', error);
            // Show error message
            if (statusContainer) {
              statusContainer.innerHTML = `
                <div style="display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px; background: rgb(239, 68, 68, 0.1); border-radius: var(--rounded-button); color: rgb(239, 68, 68);">
                  ${this.getXCircleIconSVG()}
                  <span class="text-sm font-bold">Failed to add to cart. Please try again.</span>
                </div>
              `;
              statusContainer.style.display = 'block';
            }
            if (button) {
              button.disabled = false;
              button.textContent = 'Add Battery Warranty to Cart';
            }
          }
        },

        onCancel: () => {
          // Logging removed
        },

        onError: (error) => {
          console.error('[WarrantyComparisonWidget] Error ensuring parent in cart:', error);
          alert('Failed to add warranty. Please try again.');
        }
      });

      linkingManager.ensureParentInCart();
    }

    /**
     * Build competitor comparison HTML section
     * Mobile-first design with dynamic content based on product attributes
     */
    buildCompetitorComparisonHTML() {
      const { isRiiRoo, isOneMoto, powerType, hasBattery } = this.productAttributes;
      const brandName = isOneMoto ? 'OneMoto' : isRiiRoo ? 'RiiRoo' : 'Our';

      // Determine warranty duration based on vendor and power type
      let ourWarrantyDuration = '6 months';
      if (isOneMoto) {
        ourWarrantyDuration = powerType === 'petrol' ? '30 days' : '12 months';
      } else if (isRiiRoo) {
        ourWarrantyDuration = powerType === 'petrol' ? '30 days' : '6 months';
      }

      // Build comparison rows dynamically based on product type
      const comparisonRows = this.buildDynamicComparisonRows();

      return `
        <div style="padding: 1.25rem; background: linear-gradient(135deg, rgb(var(--color-base-button) / 0.05), rgb(var(--color-base-button) / 0.02)); border-radius: var(--rounded-card); border: 2px solid rgb(var(--color-base-button) / 0.15);">

          <!-- Section Header -->
          <div style="text-align: center; margin-bottom: 1.5rem;">
            <div style="display: inline-flex; align-items: center; justify-content: center; width: 2.5rem; height: 2.5rem; background: rgb(var(--color-base-button)); border-radius: 50%; margin-bottom: 0.75rem;">
              ${this.getTrophyIconSVG()}
            </div>
            <h3 class="heading font-bold" style="font-size: 1.125rem; margin-bottom: 0.5rem; color: rgb(var(--color-drawer-text));">
              How ${brandName} Compares to Competitors
            </h3>
            <p style="font-size: 0.8125rem; max-width: 600px; margin: 0 auto; color: rgb(var(--color-drawer-text) / 0.7); line-height: 1.4;">
              We've researched the competition. Here's how our warranty stacks up.
            </p>
          </div>

          <!-- Mobile-First Comparison Cards -->
          <div class="pw-warranty-mobile-comparison" style="display: flex; flex-direction: column; gap: 1rem; margin-bottom: 1.5rem;">
            ${comparisonRows.map(row => this.buildMobileComparisonCard(row, brandName, ourWarrantyDuration)).join('')}
          </div>

          <!-- Desktop Comparison Table (hidden on mobile) -->
          <div class="pw-warranty-desktop-table" style="display: none; overflow-x: auto; margin-bottom: 1.5rem;">
            <table style="width: 100%; border-collapse: collapse; font-size: 0.8125rem;">
              <thead>
                <tr style="border-bottom: 2px solid rgb(var(--color-drawer-text) / 0.15);">
                  <th style="padding: 10px 8px; text-align: left; font-weight: 700; color: rgb(var(--color-drawer-text)); width: 40%;">Feature</th>
                  <th style="padding: 10px 8px; text-align: center; font-weight: 700; background: rgb(var(--color-base-button) / 0.1); border-radius: 8px 8px 0 0; width: 30%;">
                    <div style="display: flex; flex-direction: column; gap: 4px; align-items: center;">
                      <span style="font-size: 0.95rem;">${brandName}</span>
                      <span style="font-size: 0.65rem; color: rgb(var(--color-base-button)); font-weight: 600; text-transform: uppercase;">You</span>
                    </div>
                  </th>
                  <th style="padding: 10px 8px; text-align: center; font-weight: 600; color: rgb(var(--color-drawer-text)); width: 30%;">Typical Competitors</th>
                </tr>
              </thead>
              <tbody>
                ${comparisonRows.map(row => this.buildDesktopComparisonRow(row, brandName)).join('')}
              </tbody>
            </table>
          </div>

          <!-- Key Insights - Mobile First -->
          <div style="display: flex; flex-direction: column; gap: 0.875rem; margin-bottom: 1.25rem;">
            ${this.buildKeyInsightCards(brandName, powerType)}
          </div>

          <!-- Bottom CTA -->
          <div style="text-align: center; padding: 1rem; background: rgb(var(--color-drawer-text) / 0.02); border-radius: var(--rounded-card); border: 1px dashed rgb(var(--color-base-button) / 0.3);">
            <p style="font-size: 0.8125rem; margin: 0 0 0.75rem 0; color: rgb(var(--color-drawer-text) / 0.9); font-weight: 500; line-height: 1.5;">
              <strong>Why ${brandName} is different:</strong> We maintain a comprehensive parts compatibility matrix and full stock. When you make a warranty claim, <strong>we actually have the parts to fix it quickly</strong>.
            </p>
            <div style="display: flex; align-items: center; justify-content: center; gap: 0.75rem; flex-wrap: wrap; font-size: 0.7rem; color: rgb(var(--color-drawer-text) / 0.7);">
              ${this.getCompactCheckIconSVG()} <span>Fast resolution</span>
              ${this.getCompactCheckIconSVG()} <span>Parts in stock</span>
              ${this.getCompactCheckIconSVG()} <span>Clear terms</span>
              ${this.getCompactCheckIconSVG()} <span>Real support</span>
            </div>
          </div>

        </div>

        <!-- CSS for responsive behaviour -->
        <style>
          @media (min-width: 768px) {
            .pw-warranty-mobile-comparison {
              display: none !important;
            }
            .pw-warranty-desktop-table {
              display: block !important;
            }
          }
        </style>
      `;
    }

    /**
     * Build dynamic comparison rows based on product attributes
     */
    buildDynamicComparisonRows() {
      const { isRiiRoo, isOneMoto, powerType, hasBattery } = this.productAttributes;
      const rows = [];

      // Determine our warranty duration
      let ourDuration = '6 months';
      if (isOneMoto) {
        ourDuration = powerType === 'petrol' ? '30 days' : '12 months';
      } else if (isRiiRoo) {
        ourDuration = powerType === 'petrol' ? '30 days' : '6 months';
      }

      // Main Components Row
      rows.push({
        feature: 'Warranty Coverage',
        isKey: false,
        us: { text: ourDuration, status: 'good' },
        competitors: { text: 'Typically 6 months', status: 'neutral' }
      });

      // Battery Warranty (only for battery products)
      if (hasBattery && powerType === 'battery') {
        const ourBatteryText = isRiiRoo ? 'Not covered + 12mo option' : isOneMoto ? 'Not covered + 12mo option' : 'Not covered';
        rows.push({
          feature: 'Battery Protection',
          isKey: false,
          us: { text: ourBatteryText, status: 'good' },
          competitors: { text: 'Varies or excluded', status: 'bad' }
        });
      }

      // Parts Availability - KEY DIFFERENTIATOR
      rows.push({
        feature: 'Parts Stock',
        isKey: true,
        us: { text: 'Full Stock Available', status: 'good' },
        competitors: { text: 'Limited or no stock', status: 'bad' }
      });

      // Claim Resolution
      rows.push({
        feature: 'Claim Speed',
        isKey: false,
        us: { text: 'Fast parts first', status: 'good' },
        competitors: { text: 'Slow (2-6 weeks)', status: 'bad' }
      });

      return rows;
    }

    /**
     * Build mobile comparison card for a single feature
     */
    buildMobileComparisonCard(row, brandName, ourWarrantyDuration) {
      const keyBadge = row.isKey ? `<span style="font-size: 0.65rem; background: rgb(var(--color-base-button)); color: white; padding: 2px 6px; border-radius: 3px; font-weight: 600; text-transform: uppercase; margin-left: 6px;">Key</span>` : '';

      return `
        <div style="padding: 1rem; background: ${row.isKey ? 'rgb(var(--color-base-button) / 0.05)' : 'rgb(var(--color-drawer-text) / 0.02)'}; border-radius: var(--rounded-card); border: ${row.isKey ? '2px solid rgb(var(--color-base-button) / 0.3)' : '1px solid rgb(var(--color-drawer-text) / 0.1)'};">

          <!-- Feature Title -->
          <div style="display: flex; align-items: center; margin-bottom: 0.75rem; padding-bottom: 0.625rem; border-bottom: 1px solid rgb(var(--color-drawer-text) / 0.1);">
            <h4 style="font-size: 0.875rem; font-weight: 700; color: rgb(var(--color-drawer-text)); margin: 0; display: flex; align-items: center;">
              ${row.feature}${keyBadge}
            </h4>
          </div>

          <!-- Comparison Grid -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.625rem; font-size: 0.75rem;">

            <!-- Us -->
            <div style="padding: 0.625rem; background: rgb(var(--color-base-button) / 0.08); border-radius: 6px; border: 1px solid rgb(var(--color-base-button) / 0.2);">
              <div style="font-size: 0.65rem; font-weight: 600; color: rgb(var(--color-base-button)); text-transform: uppercase; margin-bottom: 4px;">${brandName}</div>
              <div style="display: flex; align-items: center; gap: 4px; color: rgb(var(--color-drawer-text)); font-weight: 600;">
                ${this.getStatusIcon(row.us.status, '14px')}
                <span>${row.us.text}</span>
              </div>
            </div>

            <!-- Competitors -->
            <div style="padding: 0.625rem; background: rgb(var(--color-drawer-text) / 0.02); border-radius: 6px; border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
              <div style="font-size: 0.65rem; font-weight: 600; color: rgb(var(--color-drawer-text) / 0.6); text-transform: uppercase; margin-bottom: 4px;">Competitors</div>
              <div style="display: flex; align-items: center; gap: 4px; color: rgb(var(--color-drawer-text) / 0.85); font-weight: 500;">
                ${this.getStatusIcon(row.competitors.status, '14px')}
                <span>${row.competitors.text}</span>
              </div>
            </div>

          </div>
        </div>
      `;
    }

    /**
     * Build desktop comparison table row
     */
    buildDesktopComparisonRow(row, brandName) {
      const keyBadge = row.isKey ? `<span style="font-size: 0.65rem; background: rgb(var(--color-base-button)); color: white; padding: 2px 6px; border-radius: 3px; font-weight: 600; margin-left: 6px;">KEY</span>` : '';

      return `
        <tr style="border-bottom: 1px solid rgb(var(--color-drawer-text) / 0.08); ${row.isKey ? 'background: rgb(var(--color-base-button) / 0.03);' : ''}">
          <td style="padding: 10px 8px; color: rgb(var(--color-drawer-text) / 0.9); font-weight: ${row.isKey ? '700' : '500'};">
            ${row.feature}${keyBadge}
          </td>
          <td style="padding: 10px 8px; text-align: center; background: rgb(var(--color-base-button) / 0.05); font-weight: 600;">
            <div style="display: inline-flex; align-items: center; justify-content: center; gap: 4px; color: rgb(var(--color-base-button));">
              ${this.getStatusIcon(row.us.status, '16px')}
              <span>${row.us.text}</span>
            </div>
          </td>
          <td style="padding: 10px 8px; text-align: center;">
            <div style="display: inline-flex; align-items: center; justify-content: center; gap: 4px; color: ${this.getStatusColour(row.competitors.status)};">
              ${this.getStatusIcon(row.competitors.status, '16px')}
              <span>${row.competitors.text}</span>
            </div>
          </td>
        </tr>
      `;
    }

    /**
     * Build key insight cards with dynamic content
     */
    buildKeyInsightCards(brandName, powerType) {
      // Return only our advantage card - no specific competitor names
      return `
        <div style="padding: 0.875rem; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-card); border: 2px solid rgb(var(--color-base-button) / 0.3);">
          <div style="display: flex; align-items: start; gap: 0.625rem;">
            <div style="flex-shrink: 0; width: 1.75rem; height: 1.75rem; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button)); border-radius: 50%;">
              ${this.getCompactCheckIconSVG()}
            </div>
            <div style="flex: 1;">
              <h4 class="font-bold" style="font-size: 0.8125rem; margin-bottom: 0.375rem; color: rgb(var(--color-drawer-text)); display: flex; align-items: center; gap: 4px;">
                ${this.getTrophyIconSVG('14px')}
                ${brandName}'s Advantage
              </h4>
              <p style="font-size: 0.75rem; margin: 0; color: rgb(var(--color-drawer-text) / 0.85); line-height: 1.4;">
                <strong>Full parts stock with compatibility matrix</strong> means fast repairs. We actually have the parts in stock to honour our warranty!
              </p>
            </div>
          </div>
        </div>
      `;
    }

    /**
     * Get status icon based on status type
     */
    getStatusIcon(status, size = '16px') {
      switch (status) {
        case 'good':
          return `<svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(34, 197, 94);" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>`;
        case 'bad':
          return `<svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(239, 68, 68);" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/></svg>`;
        case 'warning':
          return `<svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(255, 193, 7);" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>`;
        case 'neutral':
          return `<svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(var(--color-drawer-text) / 0.4);" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14"/></svg>`;
        case 'unknown':
          return `<svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(var(--color-drawer-text) / 0.3);" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`;
        default:
          return '';
      }
    }

    /**
     * Get status colour
     */
    getStatusColour(status) {
      switch (status) {
        case 'good':
          return 'rgb(34, 197, 94)';
        case 'bad':
          return 'rgb(239, 68, 68)';
        case 'warning':
          return 'rgb(255, 193, 7)';
        case 'neutral':
          return 'rgb(var(--color-drawer-text) / 0.7)';
        case 'unknown':
          return 'rgb(var(--color-drawer-text) / 0.5)';
        default:
          return 'rgb(var(--color-drawer-text) / 0.7)';
      }
    }

    /**
     * Get compact check icon for inline use
     */
    getCompactCheckIconSVG() {
      return `<svg style="width: 14px; height: 14px; flex-shrink: 0; color: white;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`;
    }

    /**
     * Get compact warning icon for inline use
     */
    getCompactWarningIconSVG() {
      return `<svg style="width: 14px; height: 14px; flex-shrink: 0; color: rgb(31, 41, 55);" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>`;
    }

    /**
     * Get compact X icon for inline use
     */
    getCompactXIconSVG() {
      return `<svg style="width: 14px; height: 14px; flex-shrink: 0; color: white;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"/></svg>`;
    }

    /**
     * Cleanup
     */
    destroy() {
      // Remove modal from DOM
      const modal = document.getElementById(this.modalId);
      if (modal) {
        modal.remove();
      }

      super.destroy();
    }

    // ============================================================
    // SVG ICONS
    // ============================================================

    /**
     * Shield icon (protection/warranty)
     */
    getShieldIconSVG(colourRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: 20px; height: 20px; color: rgb(${colourRGB});" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
        </svg>
      `;
    }

    /**
     * Check icon (benefits/covered)
     */
    getCheckIconSVG() {
      return `
        <svg style="flex-shrink: 0; width: 18px; height: 18px; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
        </svg>
      `;
    }

    /**
     * X icon (not covered)
     */
    getXIconSVG() {
      return `
        <svg style="flex-shrink: 0; width: 18px; height: 18px; color: rgb(239, 68, 68);" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      `;
    }

    /**
     * Check circle icon (success)
     */
    getCheckCircleIconSVG() {
      return `
        <svg style="width: 20px; height: 20px; flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * X circle icon (error)
     */
    getXCircleIconSVG() {
      return `
        <svg style="width: 20px; height: 20px; flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * Info icon
     */
    getInfoIconSVG() {
      return `
        <svg style="width: 20px; height: 20px; flex-shrink: 0; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * Arrow right icon (CTA)
     */
    getArrowRightIconSVG() {
      return `
        <svg style="width: 18px; height: 18px; color: rgb(var(--color-base-button-text));" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
        </svg>
      `;
    }

    /**
     * Calendar icon (duration)
     */
    getCalendarIconSVG(size = '18px') {
      return `
        <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
        </svg>
      `;
    }

    /**
     * Battery icon (battery warranty)
     */
    getBatteryIconSVG(size = '18px') {
      return `
        <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(var(--color-base-button));" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z"/>
        </svg>
      `;
    }

    /**
     * Savings icon (discount)
     */
    getSavingsIconSVG() {
      return `
        <svg style="width: 16px; height: 16px; color: rgb(var(--color-base-button));" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clip-rule="evenodd"/>
        </svg>
      `;
    }

    /**
     * Trophy icon (competition/winner)
     */
    getTrophyIconSVG(size = '24px') {
      return `
        <svg style="width: ${size}; height: ${size}; color: ${size === '14px' ? 'rgb(var(--color-base-button))' : 'white'};" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8H6a1 1 0 00-.553-.894l3-2A1 1 0 0010 5a1 1 0 001.553.106l3 2z"/>
          <path fill-rule="evenodd" d="M5 11a1 1 0 011-1h8a1 1 0 011 1v5a1 1 0 01-1 1H6a1 1 0 01-1-1v-5zm7 2a1 1 0 11-2 0 1 1 0 012 0z" clip-rule="evenodd"/>
        </svg>
      `;
    }

    /**
     * Warning icon (alert/caution)
     */
    getWarningIconSVG() {
      return `
        <svg style="width: 20px; height: 20px; color: rgb(31, 41, 55);" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
      `;
    }

    /**
     * Get icon for comparison table row type
     */
    getIconForType(type, size = '18px') {
      const iconColour = 'rgb(var(--color-base-button))';

      switch (type) {
        case 'calendar':
          return this.getCalendarIconSVG(size);
        case 'battery':
          return this.getBatteryIconSVG(size);
        case 'check':
          return `
            <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: ${iconColour};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          `;
        case 'shield':
          return `
            <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: ${iconColour};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
          `;
        case 'x':
          return `
            <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(239, 68, 68);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          `;
        default:
          return this.getInfoIconSVG();
      }
    }
  }

  // Register widget
  if (!window.ProductWidgets || !window.ProductWidgets.widgets) {
    window.ProductWidgets = window.ProductWidgets || {};
    window.ProductWidgets.widgets = window.ProductWidgets.widgets || {};
  }

  window.ProductWidgets.widgets.WarrantyComparisonWidget = WarrantyComparisonWidget;

})(window);
